import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Plus,
  Clock,
  MapPin,
  Camera,
  RefreshCw,
  Inbox,
  Sparkles,
  Edit2,
  Trash2,
  X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost, apiDelete } from '../shared/apiClient';
import type { Segnalazione, Squadra } from '../shared/types';
import { useToast } from '../hooks/useToast';
import { NuovaSegnalazioneModal } from '../components/NuovaSegnalazioneModal';
import { ModificaSegnalazioneModal } from '../components/ModificaSegnalazioneModal';
import { SecureImage } from '../components/SecureImage';

function parseFoto(fotoStr?: string | null): string[] {
  if (!fotoStr) return [];
  try {
    const parsed = typeof fotoStr === 'string' ? JSON.parse(fotoStr) : fotoStr;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function timeAgo(dateIso: string): string {
  const diff = Date.now() - new Date(dateIso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'adesso';
  if (mins < 60) return `${mins} min fa`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} ore fa`;
  const days = Math.floor(hours / 24);
  return `${days} gg fa`;
}

export function SegnalazioniPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<'aperte' | 'mie'>('aperte');
  const [modalNuovaOpen, setModalNuovaOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Segnalazione | null>(null);
  const [eliminaConfirmId, setEliminaConfirmId] = useState<string | null>(null);
  const [presaInCaricoConfirmId, setPresaInCaricoConfirmId] = useState<string | null>(null);
  const [selectedSegnalazione, setSelectedSegnalazione] = useState<Segnalazione | null>(null);
  const [previewFoto, setPreviewFoto] = useState<string | null>(null);

  // Squadra Me per verifica abilitazione
  const squadraQuery = useQuery({
    queryKey: ['squadra-me'],
    queryFn: async () => {
      const { data } = await apiGet<Squadra>('/squadre/me');
      return data;
    },
  });

  const isAbilitata = squadraQuery.data?.abilitaSegnalazioni ?? false;
  const miaSquadraId = squadraQuery.data?.id;

  // Query per segnalazioni
  const segnalazioniQuery = useQuery({
    queryKey: ['segnalazioni', tab],
    queryFn: async () => {
      const params: any = {};
      if (tab === 'mie') {
        params.soloMie = 'true';
      }
      const res = await apiGet<{ totale: number; segnalazioni: Segnalazione[] }>('/segnalazioni', params);
      return res.data?.segnalazioni || [];
    },
    enabled: isAbilitata,
    refetchInterval: 15000,
  });

  // Mutazione: Autoassegnazione e presa in carico
  const convertiMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiPost<{ success: boolean; chiamata: { id: string } }>(
        `/segnalazioni/${id}/converti-in-chiamata`,
        {}
      );
      return res.data;
    },
    onSuccess: (data) => {
      toast({
        type: 'success',
        message: 'Segnalazione presa in carico! Creata chiamata autoassegnata.',
      });
      queryClient.invalidateQueries({ queryKey: ['segnalazioni'] });
      queryClient.invalidateQueries({ queryKey: ['chiamate-attive'] });
      setSelectedSegnalazione(null);
      // Reindirizza al dettaglio della chiamata generata per eseguire l'intervento
      navigate(`/chiamata/${data.chiamata.id}`);
    },
    onError: (err: any) => {
      toast({
        type: 'error',
        message: err?.response?.data?.errore || 'Errore durante la presa in carico',
      });
    },
  });

  // Mutazione: Eliminazione segnalazione
  const eliminaMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiDelete(`/segnalazioni/${id}`);
    },
    onSuccess: () => {
      toast({ type: 'success', message: 'Segnalazione eliminata con successo' });
      queryClient.invalidateQueries({ queryKey: ['segnalazioni'] });
      setEliminaConfirmId(null);
      setSelectedSegnalazione(null);
    },
    onError: (err: any) => {
      toast({
        type: 'error',
        message: err?.response?.data?.errore || 'Errore durante l\'eliminazione della segnalazione',
      });
    },
  });

  if (!isAbilitata && !squadraQuery.isLoading) {
    return (
      <div className="p-6 text-center space-y-3 animate-fade-in">
        <div className="w-12 h-12 rounded-2xl bg-slate-100 text-slate-400 mx-auto flex items-center justify-center">
          <AlertTriangle className="w-6 h-6" />
        </div>
        <h3 className="font-extrabold text-slate-700 text-base">Funzione non abilitata</h3>
        <p className="text-xs text-slate-500 max-w-sm mx-auto">
          La funzione di segnalazione difformità e guasti non è attiva per la tua squadra.
          Contatta la sala operativa per abilitarla.
        </p>
      </div>
    );
  }

  const segnalazioni = segnalazioniQuery.data || [];

  return (
    <div className="space-y-4 animate-fade-in pb-16">
      {/* ── HEADER CON AZIONE PRINCIPALE ── */}
      <section className="card-section p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-amber-100 text-amber-800 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="page-title text-base sm:text-lg">Segnalazioni Guasti</h2>
              <p className="text-xs text-slate-500">
                Rilevamento anomalie e prese in carico sul territorio
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setModalNuovaOpen(true)}
            className="btn-primary py-2 px-3 text-xs font-bold gap-1.5 shadow-2xs shrink-0"
          >
            <Plus className="w-4 h-4" />
            <span>Nuova</span>
          </button>
        </div>
      </section>

      {/* ── TAB SELECTOR ── */}
      <div className="grid grid-cols-2 gap-2 p-1 bg-slate-200/70 rounded-2xl">
        <button
          type="button"
          onClick={() => { setTab('aperte'); setSelectedSegnalazione(null); }}
          className={`py-2 px-3 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5 ${
            tab === 'aperte'
              ? 'bg-white text-slate-900 shadow-2xs'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <span>Aperte sul territorio</span>
          {tab === 'aperte' && segnalazioni.length > 0 && (
            <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-amber-100 text-amber-800 font-extrabold">
              {segnalazioni.length}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => { setTab('mie'); setSelectedSegnalazione(null); }}
          className={`py-2 px-3 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5 ${
            tab === 'mie'
              ? 'bg-white text-slate-900 shadow-2xs'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <span>Le mie segnalazioni</span>
          {tab === 'mie' && segnalazioni.length > 0 && (
            <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-slate-100 text-slate-700 font-extrabold">
              {segnalazioni.length}
            </span>
          )}
        </button>
      </div>

      {/* ── LISTA SEGNALAZIONI ── */}
      {segnalazioniQuery.isLoading ? (
        <div className="p-8 text-center text-slate-400 text-xs flex items-center justify-center gap-2">
          <RefreshCw className="w-4 h-4 animate-spin text-amber-600" />
          <span>Caricamento segnalazioni…</span>
        </div>
      ) : segnalazioni.length === 0 ? (
        <section className="card-section p-8 text-center space-y-2">
          <Inbox className="w-8 h-8 text-slate-300 mx-auto" />
          <p className="text-xs font-bold text-slate-700">
            {tab === 'aperte'
              ? 'Nessuna segnalazione aperta sul territorio al momento'
              : 'Nessuna segnalazione inviata dalla tua squadra'}
          </p>
          <p className="text-[11px] text-slate-500">
            Tocca il pulsante "Nuova" per segnalare una difformità riscontrata durante il turno.
          </p>
        </section>
      ) : (
        <div className="space-y-2.5">
          {segnalazioni.map((s) => {
            const fotoList = parseFoto(s.foto);
            const isSelected = selectedSegnalazione?.id === s.id;
            const isMia = s.squadraId === miaSquadraId;

            return (
              <section
                key={s.id}
                className={`card-section p-3.5 transition border ${
                  isSelected
                    ? 'border-amber-400 bg-amber-50/20 shadow-md ring-1 ring-amber-300'
                    : 'border-slate-200/80 hover:border-slate-300'
                }`}
              >
                {/* Header card */}
                <div
                  className="cursor-pointer"
                  onClick={() => setSelectedSegnalazione(isSelected ? null : s)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="space-y-0.5 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="badge-verde text-[11px] font-extrabold shrink-0">
                          {s.impianto?.codice}
                        </span>
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                          {s.impianto?.tipo || 'IS'}
                        </span>
                        <span className="text-[11px] text-slate-400 font-medium flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {timeAgo(s.createdAt)}
                        </span>
                        {isMia && (
                          <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">
                            Tua
                          </span>
                        )}
                      </div>
                      <h4 className="text-xs sm:text-sm font-extrabold text-slate-900 leading-snug pt-0.5">
                        {s.titolo}
                      </h4>
                      <p className="text-[11px] text-slate-600 truncate font-medium">
                        {s.impianto?.nome}
                      </p>
                    </div>

                    <div className="shrink-0 flex flex-col items-end gap-1">
                      {fotoList.length > 0 && (
                        <span className="text-[10px] font-bold text-slate-500 flex items-center gap-1 bg-slate-100 px-2 py-0.5 rounded-full">
                          <Camera className="w-3 h-3 text-slate-500" />
                          {fotoList.length}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* DETTAGLIO ESPANSO */}
                {isSelected && (
                  <div className="mt-3 pt-3 border-t border-slate-100 space-y-3 animate-fade-in">
                    {/* Descrizione estesa */}
                    {s.descrizione && (
                      <div className="p-2.5 rounded-xl bg-slate-50 text-xs text-slate-700 font-medium whitespace-pre-wrap border border-slate-100">
                        <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                          Note e difformità descritte:
                        </span>
                        {s.descrizione}
                      </div>
                    )}

                    {/* Autore segnalazione */}
                    <div className="text-[11px] text-slate-500 flex items-center justify-between">
                      <span>Rilevato da: <strong>{s.squadraNome || s.segnalatoDaNome || 'Tecnico'}</strong></span>
                      {s.impianto?.lat && s.impianto?.lng && (
                        <a
                          href={`https://www.google.com/maps?q=${s.impianto.lat},${s.impianto.lng}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-emerald-700 hover:underline font-bold flex items-center gap-1"
                        >
                          <MapPin className="w-3 h-3" />
                          <span>Apri Mappa</span>
                        </a>
                      )}
                    </div>

                    {/* Galleria Foto */}
                    {fotoList.length > 0 && (
                      <div className="space-y-1">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                          Foto allegate ({fotoList.length}):
                        </span>
                        <div className="grid grid-cols-4 gap-2">
                          {fotoList.map((fn, idx) => (
                            <div
                              key={idx}
                              onClick={() => setPreviewFoto(`/api/uploads/${fn}`)}
                              className="aspect-square rounded-xl overflow-hidden border border-slate-200 bg-slate-100 cursor-pointer active:scale-95 transition"
                            >
                              <SecureImage
                                src={`/api/uploads/${fn}`}
                                alt="foto guasto"
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* PULSANTI AZIONE: PRENDI IN CARICO / MODIFICA / ELIMINA */}
                    <div className="space-y-2 pt-2">
                      <button
                        type="button"
                        disabled={convertiMutation.isPending}
                        onClick={() => setPresaInCaricoConfirmId(s.id)}
                        className="w-full btn-primary py-2.5 text-xs font-extrabold flex items-center justify-center gap-2 shadow-md bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 cursor-pointer"
                      >
                        {convertiMutation.isPending && presaInCaricoConfirmId === s.id ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            <span>Presa in carico in corso…</span>
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4" />
                            <span>⚡ Prendi in carico ed esegui intervento</span>
                          </>
                        )}
                      </button>

                      {/* Se la segnalazione appartiene a questa squadra: MODIFICA ed ELIMINA */}
                      {isMia && (
                        <div className="grid grid-cols-2 gap-2 pt-1">
                          <button
                            type="button"
                            onClick={() => setEditTarget(s)}
                            className="btn-outline py-2 text-xs font-bold flex items-center justify-center gap-1.5 text-slate-700"
                          >
                            <Edit2 className="w-3.5 h-3.5 text-sky-600" />
                            <span>Modifica</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => setEliminaConfirmId(s.id)}
                            className="btn-outline py-2 text-xs font-bold flex items-center justify-center gap-1.5 text-rose-700 border-rose-200 hover:bg-rose-50"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                            <span>Elimina</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      {/* ── MODALE NUOVA SEGNALAZIONE ── */}
      <NuovaSegnalazioneModal
        isOpen={modalNuovaOpen}
        onClose={() => setModalNuovaOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['segnalazioni'] });
        }}
      />

      {/* ── MODALE MODIFICA SEGNALAZIONE ── */}
      {editTarget && (
        <ModificaSegnalazioneModal
          isOpen={!!editTarget}
          segnalazione={editTarget}
          onClose={() => setEditTarget(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['segnalazioni'] });
            setSelectedSegnalazione(null);
          }}
        />
      )}

      {/* ── DIALOG CONFERMA PRESA IN CARICO SEGNALAZIONE ── */}
      {presaInCaricoConfirmId && (
        <div className="fixed inset-0 z-[110] bg-slate-900/70 backdrop-blur-xs flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-2xl max-w-sm w-full p-5 space-y-4 shadow-2xl border border-slate-200 animate-slide-up">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-extrabold text-slate-900 text-base">Conferma Presa in Carico</h3>
                <p className="text-xs text-slate-500">Attivazione chiamata e intervento</p>
              </div>
            </div>
            <p className="text-sm font-semibold text-slate-700 leading-relaxed">
              Prendi in carico questa segnalazione solo se hai intenzione di andare a fare l'intervento!
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setPresaInCaricoConfirmId(null)}
                disabled={convertiMutation.isPending}
                className="btn-outline px-3.5 py-2 text-xs font-bold"
              >
                annulla
              </button>
              <button
                type="button"
                onClick={() => {
                  const id = presaInCaricoConfirmId;
                  convertiMutation.mutate(id, {
                    onSettled: () => setPresaInCaricoConfirmId(null),
                  });
                }}
                disabled={convertiMutation.isPending}
                className="btn-primary bg-amber-600 hover:bg-amber-700 px-4 py-2 text-xs font-bold gap-1.5 shadow-sm"
              >
                {convertiMutation.isPending ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Sparkles className="w-3.5 h-3.5" />
                )}
                <span>prendi in carico</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── DIALOG CONFERMA ELIMINAZIONE ── */}
      {eliminaConfirmId && (
        <div className="fixed inset-0 z-[110] bg-slate-900/70 backdrop-blur-xs flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-2xl max-w-sm w-full p-5 space-y-4 shadow-2xl border border-slate-200 animate-slide-up">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center shrink-0">
                <Trash2 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-extrabold text-slate-900 text-base">Elimina Segnalazione</h3>
                <p className="text-xs text-slate-500">Operazione definitiva</p>
              </div>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              Sei sicuro di voler eliminare definitivamente questa segnalazione? Non rimarrà archiviata e verrà rimossa dal sistema.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setEliminaConfirmId(null)}
                disabled={eliminaMutation.isPending}
                className="btn-outline px-3.5 py-2 text-xs font-bold"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={() => eliminaMutation.mutate(eliminaConfirmId)}
                disabled={eliminaMutation.isPending}
                className="btn-primary bg-rose-600 hover:bg-rose-700 px-4 py-2 text-xs font-bold gap-1.5"
              >
                {eliminaMutation.isPending ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                <span>Elimina</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── LIGHTBOX INGRANDIMENTO FOTO ── */}
      {previewFoto && (
        <div
          className="fixed inset-0 z-[120] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setPreviewFoto(null)}
        >
          <button
            type="button"
            className="absolute top-4 right-4 p-2 text-white/80 hover:text-white rounded-full bg-white/10"
            onClick={() => setPreviewFoto(null)}
          >
            <X className="w-6 h-6" />
          </button>
          <div className="max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl">
            <SecureImage
              src={previewFoto}
              alt="foto grande"
              className="w-full h-full object-contain max-h-[85vh]"
            />
          </div>
        </div>
      )}
    </div>
  );
}
